// Caller-side waiting audio: while the outgoing call is ringing, the caller
// hears a voice announcement ("Ti stiamo collegando con il professionista
// desiderato") once, then a looping Italian ringback tone (425 Hz, 1s on /
// 4s off) until the professional answers or the call ends.
//
// Same architecture and race rules as ringtone.ts: HTMLAudioElement for the
// voice file, Web Audio oscillator for the ringback, and a `wantPlaying`
// guard so a stop always beats an async start resolving later. Subject to
// the iOS hardware mute switch like every web audio source.

type WindowWithWebkitAudio = Window & { webkitAudioContext?: typeof AudioContext };

const VOICE_URL = `${import.meta.env.BASE_URL}sounds/connecting-voice.mp3`;

/**
 * Pure gate used by the CallContext wiring: the tone plays only on the caller
 * side (`initiating`/`ringing` — the callee goes through `incoming`) and only
 * for calls with audio media. Chat sessions stay silent.
 */
export function shouldPlayCallerTone(state: string, type: string | null): boolean {
  return (state === 'initiating' || state === 'ringing') && (type === 'audio' || type === 'video');
}

// ---- Voice announcement element ----

let el: HTMLAudioElement | null = null;

function getEl(): HTMLAudioElement | null {
  if (typeof window === 'undefined') return null;
  if (!el) {
    try {
      el = new Audio(VOICE_URL);
      el.loop = false;
      el.preload = 'auto';
    } catch {
      return null;
    }
  }
  return el;
}

// ---- Ringback oscillator ----

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    const Ctor = window.AudioContext || (window as WindowWithWebkitAudio).webkitAudioContext;
    if (!Ctor) return null;
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
  }
  return ctx;
}

// Intended play state. Guards every async start path (voice play() promise,
// ended handler, AudioContext.resume) so a stop requested meanwhile wins.
let wantPlaying = false;

let ringbackActive: { stop: () => void } | null = null;

function startRingback(): void {
  stopRingback();
  const c = getCtx();
  if (!c) return;
  const build = () => {
    // Two start() calls can both be parked on a suspended context's resume()
    // (iOS only resolves it on a gesture); when it resolves both would build,
    // and the first oscillator would be orphaned — ringing forever, with no
    // handle left to stop it. Whoever builds last owns the ringback.
    stopRingback();
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.connect(gain);
    gain.connect(c.destination);
    osc.frequency.value = 425; // Italian ringback tone

    let playing = true;
    const timers: ReturnType<typeof setTimeout>[] = [];
    // Cadence 1s on / 4s off, gated through the gain node.
    const loop = () => {
      if (!playing) return;
      gain.gain.value = 0.1;
      timers.push(setTimeout(() => {
        if (!playing) return;
        gain.gain.value = 0;
        timers.push(setTimeout(() => { if (playing) loop(); }, 4000));
      }, 1000));
    };
    try { osc.start(); } catch { /* already started */ }
    loop();

    ringbackActive = {
      stop: () => {
        playing = false;
        timers.forEach(clearTimeout);
        try { osc.stop(); } catch { /* already stopped */ }
        try { osc.disconnect(); gain.disconnect(); } catch { /* noop */ }
      },
    };
  };
  if (c.state === 'suspended') {
    void c.resume().then(() => { if (wantPlaying) build(); }).catch(() => {});
  } else {
    build();
  }
}

function stopRingback(): void {
  ringbackActive?.stop();
  ringbackActive = null;
}

/**
 * Unlock playback on a user gesture (same iOS trick as primeRingtone). The
 * outgoing call always starts from a click so this is rarely needed, but it
 * is exposed for symmetry with the incoming ringtone.
 */
export function primeCallerTone(): void {
  const a = getEl();
  if (a) {
    const wasMuted = a.muted;
    a.muted = true;
    const p = a.play();
    if (p && typeof p.then === 'function') {
      void p
        .then(() => {
          a.pause();
          a.currentTime = 0;
          a.muted = wasMuted;
        })
        .catch(() => {
          a.muted = wasMuted;
        });
    } else {
      a.pause();
      a.currentTime = 0;
      a.muted = wasMuted;
    }
  }
  const c = getCtx();
  if (c) void c.resume().catch(() => { /* still no gesture */ });
}

/**
 * Start the waiting audio: voice once, then looping ringback. Idempotent —
 * replaces any current playback. If the voice can't play (autoplay blocked),
 * the ringback starts directly.
 */
export function startCallerTone(): void {
  stopCallerTone();
  wantPlaying = true;
  const a = getEl();
  if (!a) {
    startRingback();
    return;
  }
  a.currentTime = 0;
  a.muted = false;
  a.onended = () => {
    if (wantPlaying) startRingback();
  };
  const p = a.play();
  if (p && typeof p.then === 'function') {
    void p
      .then(() => {
        // A stop that raced ahead of this resolving play must win.
        if (!wantPlaying) { try { a.pause(); a.currentTime = 0; } catch { /* noop */ } }
      })
      .catch(() => {
        // Voice blocked — go straight to the ringback, unless stopped meanwhile.
        if (wantPlaying) startRingback();
      });
  }
}

/** Stop voice + ringback (safe to call when nothing is playing). */
export function stopCallerTone(): void {
  wantPlaying = false;
  const a = el;
  if (a) {
    a.onended = null;
    try { a.pause(); a.currentTime = 0; } catch { /* noop */ }
  }
  stopRingback();
}
